import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';

import {buildYearOptions, generateOptions, MONTH_OPTIONS} from './profileFormOptions';
import {FormSection} from '../../../../../../components/FormMaker/types';
import {EducationDegree} from '../../../../../../domain/education';
import {ApplicantProfile} from '../../../types';

export const useEducationSection = (profile: ApplicantProfile, editId?: string): FormSection[] => {
  const {t: tfe} = useTranslation('translation', {keyPrefix: 'applicantProfile.form.education'});
  const {t: tfsh} = useTranslation('translation', {keyPrefix: 'applicantProfile.form.shared'});
  const {t: tsh} = useTranslation('translation', {keyPrefix: 'shared'});
  const {t: td} = useTranslation('domain');

  const edu = useMemo(() => profile.education?.find((e) => e.id === editId), [profile.education, editId]);
  const years = useMemo(() => buildYearOptions(), []);

  return useMemo<FormSection[]>(
    () => [
      {
        type: 'title',
        data: {
          title: tfe('title'),
        },
      },
      {
        type: 'text',
        data: {
          name: 'schoolName',
          label: tfe('schoolName.label'),
          placeholder: tfe('schoolName.placeholder'),
          type: 'text',
          initialValue: edu?.schoolName,
          schema: Yup.string()
            .min(2, 'Too Short!')
            .max(64, 'Too Long!')
            .required(tsh('formValidation.required', {fieldName: 'School Name'}) || 'Required'),
        },
      },
      {
        type: 'select',
        data: {
          name: 'degree',
          placeholder: tfe('degree.label'),
          label: tfe('degree.label'),
          options: generateOptions(EducationDegree, 'eductionDegreeEnum', td),
          initialValue: edu?.degree,
        },
      },
      {
        type: 'text',
        data: {
          name: 'fieldOfStudy',
          placeholder: tfe('fieldOfStudy.placeholder'),
          label: tfe('fieldOfStudy.label'),
          type: 'text',
          initialValue: edu?.fieldOfStudy || '',
          schema: Yup.string()
            .min(2, 'Too Short!')
            .max(64, 'Too Long!')
            .when('degree', {
              is: (degree: string) => degree !== 'high_school',
              then: (schema) =>
                schema.required(tsh('formValidation.required', {fieldName: 'Field of Study'}) || 'Required'),
              otherwise: (schema) => schema.notRequired(),
            }),
        },
      },
      {
        type: 'label',
        data: {
          label: tfsh('dates.startDate'),
        },
      },
      {
        type: 'stacked',
        data: {
          sections: [
            {
              type: 'select',
              data: {
                name: 'startMonth',
                placeholder: 'Month',
                options: MONTH_OPTIONS,
                initialValue: edu?.startMonth,
                schema: Yup.number()
                  .required(tsh('formValidation.required', {fieldName: tfsh('dates.month')}) || 'Required')
                  .min(1)
                  .max(12),
              },
            },
            {
              type: 'select',
              data: {
                name: 'startYear',
                placeholder: 'Year',
                options: years,
                initialValue: edu?.startYear,
                schema: Yup.number()
                  .required(tsh('formValidation.required', {fieldName: tfsh('dates.year')}) || 'Required')
                  .min(1950)
                  .max(new Date().getFullYear()),
              },
            },
          ],
        },
      },
      {
        type: 'label',
        data: {
          label: tfsh('dates.endDate'),
        },
      },
      {
        type: 'stacked',
        data: {
          sections: [
            {
              type: 'select',
              data: {
                name: 'endMonth',
                placeholder: 'Month',
                options: MONTH_OPTIONS,
                initialValue: edu?.endMonth,
                schema: Yup.number()
                  .required(tsh('formValidation.required', {fieldName: tfsh('dates.month')}) || 'Required')
                  .min(1)
                  .max(12)
                  .when(['startYear', 'endYear'], ([startYear, endYear], schema) => {
                    if (startYear === endYear) {
                      return schema.min(Yup.ref('startMonth'), tfsh('dates.endMonthInvalid') || 'Required');
                    }
                    return schema;
                  }),
              },
            },
            {
              type: 'select',
              data: {
                name: 'endYear',
                placeholder: 'Year',
                options: years,
                initialValue: edu?.endYear,
                schema: Yup.number()
                  .required(tsh('formValidation.required', {fieldName: tfsh('dates.year')}) || 'Required')
                  .min(1950)
                  .min(Yup.ref('startYear'), tfsh('dates.endYearInvalid') || 'Required')
                  .max(new Date().getFullYear()),
              },
            },
          ],
        },
      },
    ],
    [edu, td, tfe, tfsh, tsh, years]
  );
};
